import { Flex, theme, Typography, Button } from 'antd'
import { useState } from 'react'
import { CopyOutlined, ShareAltOutlined } from '@ant-design/icons'
import { useGetMoneyFlow, useUpdateDocument } from '../api/case/document.ts'
import { useParams } from "react-router-dom"
import { EditOutlined } from "@ant-design/icons"
import { MoneyFlowControls } from "./MoneyFlowControls.tsx"
import { ReportControls } from "./Report/ReportControls.tsx"
import { useGetReport } from "../api/report/report.ts"
import { CONTROL_HEIGHT } from "../utils/constants.ts"
import { ShareMoneyFlowModal } from '../modals/ShareMoneyFlowModal/ShareMoneyFlowModal.tsx'
import { useShareMoneyflowModal } from '../modals/ShareMoneyFlowModal/useShareMoneyFlowModal.ts'
import { useCopyToClipboard } from '../hooks/useCopyToClipboard.tsx'
import { AddressMetadataLabels, usePrefetchAddressLabels } from './common/AddressMetadataLabels.tsx'

export const DocumentControls = () => {
    const {
        documentId,
        reportId
    } = useParams()
    /*const navigate = useNavigate()*/
    const { data: moneyflow } = useGetMoneyFlow(documentId)
    const { data: report } = useGetReport(reportId)
    // const { mutate: archiveDocument } = useArchiveDocument(caseId!)
    const { mutate: updateDocument } = useUpdateDocument()
    const { show } = useShareMoneyflowModal()
    const document = moneyflow || report?.data
    const { token } = theme.useToken()
    const [editing, setEditing] = useState(false)
    // The copy button copies the flow's address(es), not its whole title ("0x… MoneyFlow")
    const addresses = moneyflow?.content.addresses ?? []
    const { copyToClipboard, contextHolder } = useCopyToClipboard(
        addresses.length > 1 ? 'Addresses copied successfully' : 'Address copied successfully'
    )
    // Bitquery labels of the flow's address, when it has a single one
    const labelledAddress = addresses.length === 1 ? addresses[0] : undefined
    usePrefetchAddressLabels(labelledAddress ? [labelledAddress] : [])

    const handleDocumentNameChange = (value: string) => {
        updateDocument({
            contentType: document!.contentType,
            changes: { name: value },
            documentId: documentId || reportId as string,
        })
    }

    return (
        <Flex
            style={{
                backgroundColor: token.colorBgContainer,
                minHeight: CONTROL_HEIGHT,
                paddingInlineStart: token.paddingMD,
                paddingInlineEnd: token.paddingMD,
                paddingBlock: token.paddingXS,
                gap: token.margin
            }}
            gap={token.marginSM}
            align="center"
            wrap="wrap"
        >
            {/* Share, title, edit, copy and labels stay on one line: 360px plus
                nearly all the room the version / tracing controls leave, which
                wrap below them on a narrow screen */}
            <Flex
                align="center"
                gap={token.marginSM}
                style={{ flex: '100 1 360px', minWidth: 0 }}
            >
                {documentId && <Button
                    icon={<ShareAltOutlined />}
                    onClick={() => show()}
                />}
                {/* The title truncates with plain CSS: antd's own ellipsis is
                    measured once in JS (before the web font loads), so it cut
                    titles that fit and left long ones unclipped. Edit and copy are
                    buttons after it rather than icons inside it. */}
                <Typography.Title
                    editable={{
                        editing,
                        triggerType: [],
                        onChange: (value) => {
                            setEditing(false)
                            handleDocumentNameChange(value)
                        },
                        onCancel: () => setEditing(false),
                        onEnd: () => setEditing(false),
                        autoSize: { maxRows: 1 }
                    }}
                    level={4}
                    title={document?.name}
                    style={{
                        margin: 0,
                        minWidth: 60,
                        flex: editing ? '1 1 auto' : '0 1 auto',
                        ...(!editing && { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                    }}
                >
                    {document?.name}
                </Typography.Title>
                {!editing && <>
                    <Button
                        type="text"
                        icon={<EditOutlined style={{ fontSize: "20px", color: token.colorPrimary }} />}
                        title="Rename"
                        onClick={() => setEditing(true)}
                    />
                    {addresses.length > 0 && <>
                        {contextHolder}
                        <Button
                            type="text"
                            icon={<CopyOutlined style={{ fontSize: "18px" }} />}
                            title={addresses.length > 1 ? 'Copy addresses' : 'Copy address'}
                            onClick={() => copyToClipboard(addresses.join(', '))}
                        />
                    </>}
                    {/* Short of room, the labels give way before the title does */}
                    {labelledAddress && <div className="document-title-labels" style={{ flex: '0 1000 auto', minWidth: 48 }}>
                        <AddressMetadataLabels address={labelledAddress} singleLine />
                    </div>}
                </>}
            </Flex>
            {moneyflow && <ShareMoneyFlowModal />}
            {documentId && <MoneyFlowControls />}
            {reportId && <ReportControls />}
        </Flex>
    )
}
